import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Text,
  VStack,
  Badge,
  HStack,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Divider
} from '@chakra-ui/react';
import { COLORS } from '../utils/constants';
import { getBackendUrl } from '../config/environments';
import useAuth from '../store/useAuth';
import useCredits from '../store/useCredits';
import { FaImage, FaVideo, FaRobot } from 'react-icons/fa';
import { Icon } from '@chakra-ui/react';

// Credit package component
const CreditPackage = ({ amount, price, bonusPercentage, label, onSelect, isSelected }) => {
  const bonusCredits = Math.floor(amount * (bonusPercentage / 100));
  
  return (
    <Box
      bg={isSelected ? "rgba(0, 168, 107, 0.15)" : "rgba(0, 0, 0, 0.8)"}
      borderWidth="1px"
      borderColor={isSelected ? COLORS.accent : COLORS.primary}
      borderRadius="lg"
      p={4}
      position="relative"
      transition="all 0.3s"
      className={isSelected ? "glow-border enhanced-glow" : "glow-border"}
      cursor="pointer"
      onClick={onSelect}
      _hover={{
        borderColor: COLORS.accent,
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 168, 107, 0.3)'
      }}
    >
      {bonusPercentage > 0 && (
        <Badge
          position="absolute"
          top="-8px"
          right="-8px"
          bg={COLORS.accent}
          color="black"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="full"
          boxShadow="0 0 8px rgba(50, 205, 50, 0.5)"
          zIndex={10}
        >
          +{bonusPercentage}% BONUS
        </Badge>
      )}
      
      <VStack spacing={2} align="center">
        <Text fontSize="lg" fontWeight="bold" color={COLORS.accent} textTransform="uppercase">
          {label}
        </Text>
        
        <Text fontSize="xl" fontWeight="bold" color={COLORS.text}>
          {amount.toLocaleString()} Credits
        </Text>
        
        {bonusCredits > 0 && (
          <Text fontSize="sm" color={COLORS.accent}>
            +{bonusCredits.toLocaleString()} Bonus Credits
          </Text>
        )}
        
        <Text fontSize="md" fontWeight="bold" color={COLORS.accent}>
          {price.toLocaleString()} XGCL
        </Text>
        
        {/* <Text fontSize="xs" color={COLORS.textSecondary}>
          {((amount + bonusCredits) / price).toFixed(3)} credits per XGCL
        </Text> */}
      </VStack>
    </Box>
  );
};

const BuyCreditsModal = ({ isOpen, onClose }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [xamanPaymentData, setXamanPaymentData] = useState(null);
  const { isOpen: isPaymentOpen, onOpen: onPaymentOpen, onClose: onPaymentClose } = useDisclosure();
  
  const toast = useToast();
  const { user } = useAuth();
  const { balance, addCredits } = useCredits();

  // Credit packages
  const creditPackages = [
    { id: 'start', amount: 800, price: 2218, bonusPercentage: 0, label: 'Start' },
    { id: 'pro', amount: 2500, price: 5544, bonusPercentage: 20, label: 'Pro' },
    { id: 'saver', amount: 6000, price: 11947, bonusPercentage: 28, label: 'Saver' },
  ];
  
  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
  };
  
  const getTotalCredits = () => {
    if (selectedPackage) {
      const bonusCredits = Math.floor(selectedPackage.amount * (selectedPackage.bonusPercentage / 100));
      return selectedPackage.amount + bonusCredits;
    }
    return 0;
  };
  
  const getXgcPrice = () => {
    if (selectedPackage) {
      return selectedPackage.price;
    }
    return 0;
  };
  
  const handlePurchase = async () => {
    if (!selectedPackage) {
      toast({
        title: 'No package selected',
        description: 'Please select a credit package',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Start payment process
    console.log('🛒 [BUY_CREDITS_DEBUG] Starting payment process...');
    console.log('🛒 [BUY_CREDITS_DEBUG] User wallet:', user.walletAddress);
    console.log('🛒 [BUY_CREDITS_DEBUG] Selected package:', selectedPackage);
    console.log('🛒 [BUY_CREDITS_DEBUG] Backend URL:', getBackendUrl());
    
    setIsProcessing(true);
    
    try {
      const requestPayload = {
        walletAddress: user.walletAddress,
        packageId: selectedPackage.id,
        xgclPrice: selectedPackage.price,
        credits: selectedPackage.amount
      };
      
      console.log('🛒 [BUY_CREDITS_DEBUG] Sending request payload:', requestPayload);
      
      const res = await fetch(`${getBackendUrl()}/api/credits/purchase`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestPayload)
      });
      
      console.log('🛒 [BUY_CREDITS_DEBUG] Response status:', res.status);
      console.log('🛒 [BUY_CREDITS_DEBUG] Response headers:', Object.fromEntries(res.headers.entries()));
      
      const data = await res.json();
      console.log('🛒 [BUY_CREDITS_DEBUG] Response data:', data);
      
      if (!data.success) {
        console.error('❌ [BUY_CREDITS_DEBUG] Backend returned error:', data.error);
        throw new Error(data.error);
      }

      // Check if this is a trust line step or payment step
      console.log('🛒 [BUY_CREDITS_DEBUG] Processing response step:', data.step);
      
      if (data.step === 'trustline') {
        console.log('🔗 [BUY_CREDITS_DEBUG] Trustline required - setting up trustline flow');
        console.log('🔗 [BUY_CREDITS_DEBUG] Trustline data:', {
          uuid: data.uuid,
          qrUrl: data.qrUrl,
          deepLink: data.deepLink,
          expiresIn: data.expiresIn,
          nextStep: data.nextStep
        });
        
        // Step 1: User needs to establish trust line first
        setXamanPaymentData({
          qrCode: data.qrUrl,
          xamanPaymentUrl: data.deepLink,
          expiresIn: data.expiresIn,
          paymentId: null, // No payment ID for trust line
          step: 'trustline',
          message: data.message || 'First, you need to establish a trust line to XGCL tokens',
          nextStep: data.nextStep
        });
        onPaymentOpen();
        
        // Subscribe to trust line status instead of payment status
        console.log('🔗 [BUY_CREDITS_DEBUG] Subscribing to trustline status:', data.uuid);
        subscribeToTrustLineStatus(data.uuid, data.nextStep);
        
      } else if (data.step === 'payment') {
        console.log('💰 [BUY_CREDITS_DEBUG] Payment required - setting up payment flow');
        console.log('💰 [BUY_CREDITS_DEBUG] Payment data:', {
          paymentId: data.paymentId,
          uuid: data.uuid,
          qrUrl: data.qrUrl,
          deepLink: data.deepLink,
          expiresIn: data.expiresIn
        });
        
        // Step 2: Normal payment flow
        setXamanPaymentData({
          qrCode: data.qrUrl,
          xamanPaymentUrl: data.deepLink,
          expiresIn: data.expiresIn,
          paymentId: data.paymentId,
          step: 'payment',
          message: 'Complete your payment to receive credits'
        });
        onPaymentOpen();
        
        console.log('💰 [BUY_CREDITS_DEBUG] Subscribing to payment status:', data.paymentId);
        subscribeToPaymentStatus(data.paymentId);
        
      } else {
        console.error('❌ [BUY_CREDITS_DEBUG] Unknown response step:', data.step);
        throw new Error('Unknown response step from backend');
      }

    } catch (err) {
      console.error('❌ [BUY_CREDITS_DEBUG] Payment error:', {
        message: err.message,
        stack: err.stack,
        walletAddress: user.walletAddress,
        packageId: selectedPackage.id
      });
      toast({ 
        title: "Payment error", 
        description: err.message, 
        status: "error",
        duration: 4000,
        isClosable: true
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handlePaymentSuccess = (creditsAdded) => {
    const totalCredits = creditsAdded || getTotalCredits();
    
    // Add credits to user account
    addCredits(totalCredits);
    
    // Close the modals
    onPaymentClose();
    onClose();
    
    // Show success message
    toast({
      title: 'Payment successful',
      description: `${totalCredits} credits have been added to your account!`,
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    
    // Reset selected package
    setSelectedPackage(null);
  };

  const subscribeToPaymentStatus = (paymentId) => {
    const es = new EventSource(`${getBackendUrl()}/api/payments/${paymentId}/subscribe`);
    es.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type === "paid" || (msg.type === "status" && msg.creditsAdded)) {
        handlePaymentSuccess(msg.creditsAdded);
        es.close();
      }
      if (msg.type === "error" || (msg.type === "status" && msg.error)) {
        handlePaymentError(msg.error || msg.message);
        es.close();
      }
    };
  };

  const subscribeToTrustLineStatus = (uuid, nextStep) => {
    // Subscribe to XUMM payload status for trust line
    const es = new EventSource(`${getBackendUrl()}/api/xumm/payload/${uuid}/subscribe`);
    es.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      
      if (msg.data && msg.data.signed && !msg.data.cancelled && !msg.data.expired) {
        // Trust line was successfully signed
        es.close();
        
        // Close current modal and show success message
        onPaymentClose();
        
        toast({
          title: 'Trust line established!',
          description: 'Your trust line to XGCL tokens has been successfully created.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // If this was triggered from purchase flow, retry the purchase
        if (nextStep) {
          setTimeout(() => {
            handlePurchase();
          }, 2000);
        }
        
      } else if (msg.data && (msg.data.cancelled || msg.data.expired)) {
        // Trust line was cancelled or expired
        es.close();
        handlePaymentError('Trust line setup was cancelled or expired');
      }
    };
    
    es.onerror = () => {
      es.close();
      handlePaymentError('Connection error while waiting for trust line');
    };
  };

  const handlePaymentError = (errorMessage) => {
    // Close payment modal
    onPaymentClose();
    
    // Show error message
    toast({
      title: 'Payment failed',
      description: errorMessage || 'Payment could not be completed',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedPackage(null);
      setIsProcessing(false);
      setXamanPaymentData(null);
    }
  }, [isOpen]);

  return (
    <>
      {/* Main Buy Credits Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
        <ModalOverlay backdropFilter="blur(10px)" bg="blackAlpha.800" />
        <ModalContent 
          bg="rgba(0, 0, 0, 0.95)" 
          borderColor={COLORS.primary} 
          borderWidth="1px"
          borderRadius="lg"
          boxShadow="0 20px 60px rgba(0, 0, 0, 0.8), 0 0 30px rgba(0, 168, 107, 0.2)"
          backdropFilter="blur(20px)"
          maxH="90vh"
          overflow="auto"
        >
          <ModalHeader 
            color={COLORS.text}
            borderBottomWidth="1px"
            borderColor="rgba(0, 168, 107, 0.2)"
            pb={4}
          >
            <Flex align="center" justify="space-between">
              <Heading 
                size="lg" 
                bgGradient="linear(to-r, #00ff7f, #32cd32)"
                bgClip="text"
                className="glow-text"
                fontFamily="Space Grotesk"
              >
                Buy Credits
              </Heading>
              <Badge 
                px={3} 
                py={1} 
                borderRadius="md" 
                fontWeight="bold"
                bg="rgba(0, 168, 107, 0.15)"
                border="1px solid"
                borderColor={COLORS.primary}
                color={COLORS.accent}
              >
                Current: {balance} Credits
              </Badge>
            </Flex>
          </ModalHeader>
          
          <ModalCloseButton color={COLORS.text} />
          
          <ModalBody py={6}>
            <VStack spacing={6} align="stretch">
              {/* Credit Usage Information */}
              <Box 
                bg="rgba(0, 0, 0, 0.8)" 
                p={4} 
                borderRadius="lg"
                borderWidth="1px"
                borderColor="rgba(0, 168, 107, 0.2)"
                className="glow-border"
              >
                <Heading size="md" className="glow-text" mb={3} color={COLORS.text}>
                  Credit Usage Guide
                </Heading>
                <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
                  <Box>
                    <HStack mb={2}>
                      <Icon as={FaImage} color={COLORS.primary} boxSize={4} />
                      <Heading size="sm" color={COLORS.text}>Image Generation</Heading>
                    </HStack>
                    <Text color={COLORS.textSecondary} fontSize="sm">
                      • Standard: 10 credit
                    </Text>
                    <Text color={COLORS.textSecondary} fontSize="sm">
                      • HD: 25 credits
                    </Text>

                  </Box>
                  
                  <Box>
                    <HStack mb={2}>
                      <Icon as={FaVideo} color={COLORS.primary} boxSize={4} />
                      <Heading size="sm" color={COLORS.text}>Video Generation</Heading>
                    </HStack>
                    <Text color={COLORS.textSecondary} fontSize="sm">
                      • 5s: 120 credits
                    </Text>
                    <Text color={COLORS.textSecondary} fontSize="sm">
                      • 10s: 240 credits
                    </Text>

                  </Box>
                  
                  <Box>
                    <HStack mb={2}>
                      <Icon as={FaRobot} color={COLORS.primary} boxSize={4} />
                      <Heading size="sm" color={COLORS.text}>AI Chat</Heading>
                    </HStack>
                    <Text color={COLORS.textSecondary} fontSize="sm">
                      • 1 credits per message
                    </Text>
                    <Text color={COLORS.textSecondary} fontSize="sm">
                      • Based on message length
                    </Text>

                  </Box>
                </Grid>
              </Box>
              
              {/* Credit Packages */}
              <Box>
                <Heading size="md" mb={4} className="glow-text" color={COLORS.text}>
                  Select Credit Package
                </Heading>
                
                <Grid 
                  templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                  gap={4}
                  mb={6}
                >
                  {creditPackages.map((pkg) => (
                    <CreditPackage
                      key={pkg.id}
                      amount={pkg.amount}
                      price={pkg.price}
                      bonusPercentage={pkg.bonusPercentage}
                      label={pkg.label}
                      isSelected={selectedPackage?.id === pkg.id}
                      onSelect={() => handleSelectPackage(pkg)}
                    />
                  ))}
                </Grid>
              </Box>
              
              {/* Pricing Summary */}
              <Box 
                bg="rgba(0, 0, 0, 0.8)" 
                p={4} 
                borderRadius="lg"
                borderWidth="1px"
                borderColor="rgba(0, 168, 107, 0.2)"
                className="glow-border"
              >
                <Heading size="md" mb={4} className="glow-text" color={COLORS.text}>
                  Summary
                </Heading>
                
                <Flex justify="space-between" mb={2}>
                  <Text color={COLORS.textSecondary}>Total Credits:</Text>
                  <Text color={COLORS.text} fontWeight="bold">{getTotalCredits().toLocaleString()}</Text>
                </Flex>
                
                <Flex justify="space-between" mb={4}>
                  <Text color={COLORS.textSecondary}>Price in XGCL:</Text>
                  <Text color={COLORS.text} fontWeight="bold">{getXgcPrice().toLocaleString()} XGCL</Text>
                </Flex>
              </Box>
            </VStack>
          </ModalBody>
          
          <ModalFooter borderTopWidth="1px" borderColor="rgba(0, 168, 107, 0.2)" pt={4}>
            <Box width="100%">
              <Button
                onClick={handlePurchase}
                isLoading={isProcessing}
                loadingText="Processing..."
                isDisabled={!selectedPackage}
                size="lg"
                className="primary-button"
                bg="linear-gradient(135deg, #00ff7f 0%, #32cd32 100%)"
                color="black"
                fontWeight="bold"
                width="100%"
                mb={{ base: 3, md: 4 }}
                _hover={{
                  bg: "linear-gradient(135deg, #32cd32 0%, #00ff7f 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(0, 255, 127, 0.4)"
                }}
                _active={{
                  transform: "translateY(0)"
                }}
              >
                Purchase with XGCL
              </Button>
              <Flex direction="row" gap={3} width="100%">
                <Button
                  onClick={() => window.open('https://xmagnetic.org/tokens/XGCL+rXGCL7F4P5tC2GnSDw9zsWpg663q29eeb?network=mainnet', '_blank')}
                  variant="outline"
                  color={COLORS.primary}
                  borderColor={COLORS.primary}
                  fontWeight={"700"}
                  padding={"10px"}
                  fontWeight={"700"}
                  borderStyle={"dashed"}
                  size="lg"
                  flex="1"
                  _hover={{ 
                    bg: 'rgba(30, 220, 11, 0.1)', 
                    color: COLORS.accent,
                    borderColor: COLORS.accent
                  }}
                >
                  Buy XGCL
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      // Make API call to get actual trustline QR code with force=true
                      const res = await fetch(`${getBackendUrl()}/api/trustline/create`, {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json"
                        },
                        body: JSON.stringify({
                          walletAddress: user.walletAddress,
                          force: true // Force create trustline even if one exists
                        })
                      });
                      const data = await res.json();
                      
                      if (!data.success) {
                        if (data.error === 'Trust line already exists for this wallet and token configuration' && data.hint) {
                          // If user manually clicked, try again with force
                          toast({
                            title: 'Trust line exists',
                            description: 'Existing trust line detected. Creating new one anyway...',
                            status: 'info',
                            duration: 3000,
                            isClosable: true,
                          });
                          return;
                        }
                        throw new Error(data.error);
                      }
                      
                      // Use the real trustline data from backend
                      setXamanPaymentData({
                        qrCode: data.qrUrl,
                        xamanPaymentUrl: data.deepLink,
                        step: 'trustline',
                        message: data.message || 'Setup trustline to XGCL tokens',
                      });
                      onPaymentOpen();
                      
                      // Subscribe to trustline status
                      subscribeToTrustLineStatus(data.uuid);
                      
                    } catch (err) {
                      toast({
                        title: 'Trustline setup error',
                        description: err.message,
                        status: 'error',
                        duration: 4000,
                        isClosable: true,
                      });
                    }
                  }}
                  variant="outline"
                  color={COLORS.accent}
                  borderColor={COLORS.accent}
                  size="lg"
                  flex="1"
                  padding={"10px"}
                  fontWeight={"700"}
                  borderStyle={"dashed"}
                  _hover={{ bg: 'rgba(0, 168, 107, 0.1)', color: COLORS.primary }}
                >
                  Setup Trustline
                </Button>
              </Flex>
            </Box>
            
            {/* <Text fontSize="xs" textAlign="center" mt={2} color={COLORS.textSecondary} width="100%">
              Secure payment processed via XAMAN wallet
            </Text> */}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Payment Modal */}
      <Modal isOpen={isPaymentOpen} onClose={onPaymentClose} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(10px)" bg="blackAlpha.700" />
        <ModalContent 
          bg="rgba(0, 0, 0, 0.95)" 
          borderColor={COLORS.primary} 
          borderWidth="1px"
          borderRadius="lg"
          boxShadow="0 20px 60px rgba(0, 0, 0, 0.8)"
          backdropFilter="blur(20px)"
        >
          <ModalHeader color={COLORS.text}>
            {xamanPaymentData?.step === 'trustline' ? 'Setup Trust Line' : 'Complete Payment'}
          </ModalHeader>
          <ModalCloseButton color={COLORS.text} />
          
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Box textAlign="center" py={2}>
                <Text color={COLORS.textSecondary} mb={4}>
                  {xamanPaymentData?.step === 'trustline' 
                    ? xamanPaymentData.message || 'First, you need to establish a trust line to XGCL tokens'
                    : 'Scan this QR code with your XAMAN wallet or click the button below to open XAMAN'
                  }
                </Text>
                
                {/* Display actual QR code from backend */}
                <Box 
                  width="200px" 
                  height="200px" 
                  margin="0 auto" 
                  border="1px solid"
                  borderColor={COLORS.primary}
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  className="data-flow"
                  position="relative"
                  overflow="hidden"
                >
                  {xamanPaymentData?.qrCode ? (
                    <img 
                      src={xamanPaymentData.qrCode} 
                      alt="Payment QR Code" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Text color="gray.500" fontSize="sm">Loading QR Code...</Text>
                  )}
                  <Box 
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    borderRadius="md"
                    bg="rgba(30, 220, 11, 0.1)"
                    pointerEvents="none"
                  />
                </Box>
              </Box>
              
              <Button
                className="outline-button"
                onClick={() => window.open(xamanPaymentData?.xamanPaymentUrl || 'https://xaman.app', '_blank')}
                borderColor={COLORS.primary}
                color={"black"}
                _hover={{
                  bg: "rgba(0, 168, 107, 0.1)",
                  color: COLORS.primary,
                }}
                isDisabled={!xamanPaymentData?.xamanPaymentUrl}
              >
                {xamanPaymentData?.step === 'trustline' ? 'Open in XAMAN - Setup Trust Line' : 'Open in XAMAN Wallet'}
              </Button>
              
              <Divider borderColor="rgba(30, 220, 11, 0.3)" />
              
              <Box textAlign="center">
                <Text color={COLORS.textSecondary}>
                  {xamanPaymentData?.step === 'trustline' 
                    ? 'Waiting for trust line confirmation...'
                    : 'Waiting for payment confirmation...'
                  }
                </Text>
                <HStack justify="center" mt={2}>
                  <span className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </HStack>
              </Box>
              
              <Text fontSize="xs" color={COLORS.textSecondary} textAlign="center">
                Payment will be automatically detected when completed.
                Do not close this window.
              </Text>
            </VStack>
          </ModalBody>
          
          <ModalFooter>
            <Button 
              onClick={onPaymentClose} 
              variant="ghost" 
              color={COLORS.textSecondary}
              _hover={{ color: COLORS.text, bg: "rgba(0, 168, 107, 0.1)" }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BuyCreditsModal;
